/**
 * ScenariosPage – picker for guided role-plays.
 */
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CaretLeft, Clock, Star } from '@phosphor-icons/react';
import { http, apiErrorMessage } from '@/lib/api';

const DIFFICULTY_COLOR = {
  easy: 'lf-chip-success',
  medium: 'lf-chip-accent',
  hard: 'lf-chip-primary',
};

export default function ScenariosPage() {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get('/scenarios');
        setScenarios(data);
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, []);

  const categories = Array.from(new Set(scenarios.map((s) => s.category)));
  const visible = filter === 'all' ? scenarios : scenarios.filter((s) => s.category === filter);

  async function start(scenario) {
    // Route through the Prep Screen so the learner sees intro + focus words
    // before the live session begins. PrepScreen POSTs to /session/start.
    navigate(`/prep?mode=scenario&scenario_id=${encodeURIComponent(scenario.id)}`);
  }

  return (
    <div className="lf-shell flex flex-col px-6 py-8" data-testid="scenarios-page">
      <div className="flex items-center gap-2">
        <Link to="/home" className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]" data-testid="scenarios-back">
          <CaretLeft size={18} />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Pick one</div>
          <h1 className="text-2xl font-bold">Guided Scenarios</h1>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="scenarios-filters">
        <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>All</FilterPill>
        {categories.map((c) => (
          <FilterPill key={c} active={filter === c} onClick={() => setFilter(c)}>{c}</FilterPill>
        ))}
      </div>

      {error ? <div className="mt-4 text-sm text-[var(--lf-destructive)]">{error}</div> : null}

      <div className="mt-5 space-y-3">
        {visible.map((s) => (
          <button
            key={s.id}
            onClick={() => start(s)}
            disabled={busy}
            className="lf-card w-full p-4 text-left transition-all duration-200 hover:-translate-y-0.5"
            data-testid={`scenario-${s.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">{s.category}</div>
                <div className="mt-1 text-lg font-bold">{s.title}</div>
                <div className="mt-1 text-sm text-[var(--lf-text-muted)]">{s.description}</div>
              </div>
              <span className={`lf-chip ${DIFFICULTY_COLOR[s.difficulty_band] || ''}`}>
                <Star size={12} weight="fill" /> {s.difficulty_band}
              </span>
            </div>
            <div className="mt-3 inline-flex items-center gap-1 text-xs text-[var(--lf-text-muted)]">
              <Clock size={12} /> ~{s.estimated_length_minutes} min
            </div>
          </button>
        ))}
      </div>

      <div className="h-10" />
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-all ${
        active
          ? 'bg-[var(--lf-primary)] text-white'
          : 'bg-[var(--lf-surface)] border border-[var(--lf-border)] text-[var(--lf-text)]'
      }`}
    >
      {children}
    </button>
  );
}
